import { ChangeDetectionStrategy, Component, inject, Input } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { MatBadge } from '@angular/material/badge'
import { MatButton } from '@angular/material/button'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatIcon } from '@angular/material/icon'
import { Instant } from '@js-joda/core'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { BehaviorSubject, Observable, of } from 'rxjs'
import { filter, map, switchMap } from 'rxjs/operators'
import {
  WorkItemConstraintsDialogComponent,
  WorkItemFilterDialogData,
} from '../../../../orders/work-items/work-item-filter-dialog/work-item-constraints-dialog.component'
import { Property } from '../../../../properties/property'
import { PropertyCache } from '../../../../properties/property-cache'
import { TerminalRoutingService } from '../../../../terminals/terminal-routing.service'
import { ConstraintInput } from '../../../workcenters/capability/constraints/constraintInput'
import { WorkStationConfiguration } from '../../configurations/work-station-configuration'
import { WorkStation } from '../../work-station'
import { WorkStationService } from '../../work-station.service'

@Component({
  selector: 'app-terminal-ws-configuration',
  templateUrl: './terminal-ws-configuration.component.html',
  styleUrls: ['./terminal-ws-configuration.component.scss'],
  providers: [PropertyCache],
  imports: [MatButton, MatBadge, MatIcon, ElderTranslatePipe],
})
export class TerminalWsConfigurationComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly _configurationsPopulated$ = new BehaviorSubject<WorkStationConfiguration[]>([])
  private readonly _activeWorkStation$ = new BehaviorSubject<WorkStation>(null)
  private propertyCache = inject(PropertyCache)
  private terminalRoutingService = inject(TerminalRoutingService)
  private workstationService = inject(WorkStationService)
  private matDialog = inject(MatDialog)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    this._activeWorkStation$
      .pipe(
        takeUntilDestroyed(),
        filter((ws) => !!ws),
        map((ws) => ws.configurations),
        switchMap((configs) => this.populateConfigs(configs))
      )
      .subscribe((configs) => this._configurationsPopulated$.next(configs))
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get activeWorkStation(): WorkStation {
    return this._activeWorkStation$.getValue()
  }

  @Input()
  public set activeWorkStation(ws: WorkStation) {
    this._activeWorkStation$.next(ws)
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public hasConfigurations(): boolean {
    return (
      this.activeWorkStation?.configurations && this.activeWorkStation?.configurations?.length > 0
    )
  }

  public numberOfAppliedFilters(): number {
    return this.activeWorkStation?.configurations.filter(
      (config) => config?.activeConf?.values?.length > 0 && config?.activeConf?.values[0] != 'false'
    ).length
  }

  public openFilterDialog(): void {
    const configurations = this._configurationsPopulated$.getValue()
    if (configurations.length > 0) {
      const constraints = configurations.map((conf) => this.buildConstraintInput(conf))
      this.openPositionFilterDialog(constraints)
        .afterClosed()
        .subscribe((changes) => {
          if (!!changes && changes.length > 0) {
            this.onWsConfigChange(changes, this.activeWorkStation)
          }
        })
    }
  }

  public buildConstraintInput(conf: WorkStationConfiguration): ConstraintInput {
    return new ConstraintInput(conf.spec, conf.activeConf.values, conf.possibleValues)
  }

  public onWsConfigChange(changes: ConstraintInput[], activeWorkStation: WorkStation) {
    changes.forEach((change) =>
      this.updateConfigurationsInPlace(change, activeWorkStation.configurations)
    )
    this.workstationService
      .update({ ...activeWorkStation, configurations: activeWorkStation.configurations })
      .subscribe((_) => {
        this.terminalRoutingService.updateQueryParams({ currentTime: Instant.now() }) // hack to refresh table filters and terminal
      })
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  public openPositionFilterDialog(
    constraints: ConstraintInput[]
  ): MatDialogRef<WorkItemConstraintsDialogComponent> {
    return this.matDialog.open(WorkItemConstraintsDialogComponent, {
      minWidth: '50vw',
      height: '40vh',
      data: <WorkItemFilterDialogData>{
        constraints: constraints,
      },
    })
  }

  private updateConfigurationsInPlace(
    changed: ConstraintInput,
    configurations: WorkStationConfiguration[]
  ): WorkStationConfiguration[] {
    const config = configurations.find((conf) => conf.spec.id === changed.spec.id)
    if (!!config) {
      config.activeConf.values = [...changed.values]
    }
    return configurations
  }

  private populateConfigs(
    configs: WorkStationConfiguration[]
  ): Observable<WorkStationConfiguration[]> {
    if (configs?.length === 0) {
      return of([])
    }
    return this.propertyCache.populateObjects(
      configs,
      (conf: WorkStationConfiguration) => conf.spec.propertyId,
      (conf: WorkStationConfiguration, prop: Property) => (conf.spec.property = prop)
    )
  }
}
